import { useEffect, useRef, useState } from 'react';

/** Start loading this far before the trigger scrolls into view. */
const PREFETCH_MARGIN = '600px';

/** The nearest ancestor that scrolls (the workspace's centre column), or null for the viewport.
 *  rootMargin only extends the *root*: observed against the viewport, a trigger clipped by a
 *  scrolling ancestor would count as "near" only once actually visible, defeating the prefetch. */
export function scrollParent(element: Element): Element | null {
  for (let node = element.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
  }
  return null;
}

/**
 * #682/#684 -- continuous scrolling: calls `onApproach` when the returned ref's element (a "Load
 * more" / "Show more" button, kept for keyboard and screen-reader users) comes within
 * PREFETCH_MARGIN of the viewport. `itemCount` re-arms the observer after each load: an observer
 * only fires on a *change* of visibility, so a trigger still in view after a page lands would
 * otherwise never ask for the next one.
 */
export function useLoadMoreOnApproach<T extends Element>(
  enabled: boolean,
  itemCount: number,
  onApproach: () => void
): (node: T | null) => void {
  const [trigger, setTrigger] = useState<T | null>(null);
  // Latest callback without re-creating the observer on every render.
  const callback = useRef(onApproach);
  useEffect(() => {
    callback.current = onApproach;
  }, [onApproach]);

  useEffect(() => {
    if (!trigger || !enabled || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) callback.current();
      },
      { root: scrollParent(trigger), rootMargin: PREFETCH_MARGIN }
    );
    observer.observe(trigger);
    return () => observer.disconnect();
  }, [trigger, enabled, itemCount]);

  return setTrigger;
}
